import { useState } from 'react';
import Rodal from 'rodal';
import { Button } from '../../../../shared/components/Button';
import '../../../../shared/styles/Orchestrator.css';
import 'rodal/lib/rodal.css';
import { TriangleAlert } from 'lucide-react';

const CONFIRM_CLICKS = 3;

function ConfirmButton({ label, confirmLabel, type, onConfirm }) {
  const [clicksLeft, setClicksLeft] = useState(CONFIRM_CLICKS);

  const handleClick = () => {
    if (clicksLeft <= 1) {
      setClicksLeft(CONFIRM_CLICKS);
      if (typeof onConfirm === 'function') onConfirm();
    } else {
      setClicksLeft((prev) => prev - 1);
    }
  };

  return (
    <Button
      label={
        clicksLeft < CONFIRM_CLICKS
          ? `Press ${clicksLeft} more time${clicksLeft !== 1 ? 's' : ''} to ${confirmLabel}`
          : label
      }
      type={type}
      onPress={handleClick}
    />
  );
}

export default function GameplaySyncErrorModal({
  visible,
  mode,
  onClose,
  onStoreToCloud,
  onLoadFromCloud,
  onDisableSync,
}) {
  const isDark = mode === 'dark';
  const borderColor = isDark ? '2px solid var(--onyx)' : '2px solid #e4e7c5';

  return (
    <Rodal
      animation='slideUp'
      visible={visible}
      onClose={onClose}
      className={isDark ? 'df_rodal_dark' : ''}
      customStyles={{
        border: borderColor,
        backgroundColor: isDark ? 'var(--jet)' : '#f6f7eb',
        color: isDark ? 'var(--ivory)' : 'var(--jet)',
        width: 520,
        height: 520,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        className={`trialment_login_form ${isDark ? 'df_modal_form_dark' : ''}`}
        style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            marginBottom: 6,
          }}
        >
          <TriangleAlert size={20} color='#e94f37' />
          <span style={{ fontWeight: 700, fontSize: 16 }}>Cloud Sync Error</span>
        </div>

        <div style={{ fontSize: 13, lineHeight: 1.5, opacity: 0.9 }}>
          We couldn&apos;t load or store your scenes. Choose an action below:
        </div>

        {/* Action 1: Store to cloud */}
        <div
          style={{
            padding: 10,
            borderRadius: 6,
            border: isDark ? '1px solid var(--onyx)' : '1px solid #dcdcdc',
          }}
        >
          <div style={{ fontSize: 12, marginBottom: 6, opacity: 0.7 }}>
            Store your current local scenes to the cloud.
          </div>
          <ConfirmButton
            label='Store Scenes to Cloud'
            confirmLabel='Store your Scenes'
            type={isDark ? 'onyx' : 'cinnabar'}
            onConfirm={onStoreToCloud}
          />
        </div>

        {/* Action 2: Load from cloud */}
        <div
          style={{
            padding: 10,
            borderRadius: 6,
            border: isDark ? '1px solid var(--onyx)' : '1px solid #dcdcdc',
          }}
        >
          <div style={{ fontSize: 12, marginBottom: 6, opacity: 0.7 }}>
            Reload your scenes from the cloud. Warning: any unsynced local changes will be
            overridden.
          </div>
          <ConfirmButton
            label='Load Scenes from Cloud'
            confirmLabel='Load your Scenes again'
            type={isDark ? 'onyx' : 'cinnabar'}
            onConfirm={onLoadFromCloud}
          />
        </div>

        {/* Action 3: Disable sync */}
        <div
          style={{
            padding: 10,
            borderRadius: 6,
            border: isDark ? '1px solid var(--onyx)' : '1px solid #dcdcdc',
          }}
        >
          <div style={{ fontSize: 12, marginBottom: 6, opacity: 0.7 }}>
            Disable gameplay syncing. Your scenes will no longer be synced to the cloud.
          </div>
          <ConfirmButton
            label='Disable Gameplay Syncing'
            confirmLabel='Disable gameplay syncing'
            type='tomato'
            onConfirm={onDisableSync}
          />
        </div>

        <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            label='Close'
            type={isDark ? 'onyx' : 'cinnabar'}
            onPress={onClose}
          />
        </div>
      </div>
    </Rodal>
  );
}
